import { useEffect, useRef, useState } from "react";

export interface SliderImage {
  id: string;
  image: string;
  /** Real pixel dimensions of `image` — passed through as width/height
   * attributes so the browser can reserve layout space and, for the
   * first (LCP) slide, prioritize the fetch without waiting for image
   * bytes to arrive to learn its aspect ratio. */
  width?: number;
  height?: number;
  // CSS object-position (e.g. "center 20%") — the slider crops full-bleed
  // with object-cover, so on a very wide/short viewport a tall or square
  // source photo gets its top and bottom cut to the vertical center by
  // default. Set this when the photo's real subject sits off that center
  // band, so the crop keeps the subject instead of empty floor/ceiling.
  position?: string;
}

const AUTOPLAY_INTERVAL_MS = 4500;

/**
 * Full-bleed autoplaying image carousel — originally the catalog page's
 * top banner (see git history), now shared with anywhere that wants a
 * slowly-cycling photo background. `interactive` (default true) gates the
 * click-anywhere-to-navigate behavior: on for a banner the visitor browses
 * directly, off for a purely decorative background sitting behind other
 * content (e.g. the homepage's closing CTA section) where a stray click
 * shouldn't change what's behind it.
 */
export default function ImageSlider({
  images,
  className = "",
  interactive = true,
}: {
  images: SliderImage[];
  className?: string;
  interactive?: boolean;
}) {
  const count = images.length;
  // Same clone-wrap infinite-loop technique as the homepage portfolio
  // slider (see PortfolioGrid.tsx) — only worth it once there's more than
  // one image, so a single image just renders as a static banner below.
  const slides = count > 1 ? [images[count - 1], ...images, images[0]] : images;

  const [index, setIndex] = useState(1);
  const [animated, setAnimated] = useState(true);
  const autoplayTimer = useRef<number>();
  const autoplayPaused = useRef(false);

  // Wraps into the valid clone range [0, count+1] on every step, instead of
  // incrementing without bound — see PortfolioGrid.tsx (same technique) for
  // why: background tabs can skip the transitionend-driven snap-back below
  // for long stretches, and without this wrap `index` would drift past the
  // end of `slides` while the tab is away.
  const wrapIndex = (i: number) => {
    if (i > count + 1) return 1;
    if (i < 0) return count;
    return i;
  };

  const restartAutoplay = () => {
    window.clearInterval(autoplayTimer.current);
    if (count <= 1) return;
    autoplayTimer.current = window.setInterval(() => {
      if (!autoplayPaused.current) setIndex((i) => wrapIndex(i + 1));
    }, AUTOPLAY_INTERVAL_MS);
  };

  useEffect(() => {
    restartAutoplay();
    return () => window.clearInterval(autoplayTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (direction: 1 | -1) => {
    setAnimated(true);
    setIndex((i) => wrapIndex(i + direction));
    restartAutoplay();
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (index === 0) {
      setAnimated(false);
      setIndex(count);
    } else if (index === count + 1) {
      setAnimated(false);
      setIndex(1);
    }
  };

  useEffect(() => {
    if (animated) return;
    const id = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(id);
  }, [animated]);

  // Left half of the image steps back, right half steps forward — no
  // visible arrow buttons, the whole image is the control.
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || count <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickedLeftHalf = e.clientX - rect.left < rect.width / 2;
    goTo(clickedLeftHalf ? -1 : 1);
  };

  if (count === 0) return null;

  return (
    <div
      className={`relative h-full w-full overflow-hidden bg-steel-100 ${className}`}
      onMouseEnter={() => {
        autoplayPaused.current = true;
      }}
      onMouseLeave={() => {
        autoplayPaused.current = false;
      }}
      onClick={handleClick}
    >
      <div
        className={`flex h-full ${count > 1 && animated ? "transition-transform duration-[600ms] ease-engineered" : ""} ${interactive && count > 1 ? "cursor-pointer" : ""}`}
        style={{ transform: count > 1 ? `translateX(-${index * 100}%)` : undefined }}
        onTransitionEnd={handleTransitionEnd}
      >
        {slides.map((slide, i) => {
          // i === 1 is the first real slide (index 0 is the clone of the
          // last one, used for the infinite-loop wrap) — the only one
          // visible on initial paint, so it's the actual LCP candidate.
          // Every other slide is off-screen until the user interacts.
          const isInitialSlide = i === 1;
          return (
            <div key={`${slide.id}-${i}`} className="h-full w-full shrink-0">
              <img
                src={slide.image}
                alt=""
                width={slide.width}
                height={slide.height}
                className="h-full w-full object-cover"
                style={slide.position ? { objectPosition: slide.position } : undefined}
                draggable={false}
                loading={isInitialSlide ? "eager" : "lazy"}
                fetchPriority={isInitialSlide ? "high" : "low"}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
